import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api, { formatApiError } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // null = checking, false = unauthenticated, object = user
  const [user, setUser] = useState(null);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const loginStep1 = useCallback(async (email, password) => {
    try {
      const { data } = await api.post('/auth/login', { email, password });
      // If backend skipped OTP (trusted device), it returns step="authenticated"
      // with the user object — set state immediately so caller can short-circuit.
      if (data.step === 'authenticated' && data.user) {
        setUser(data.user);
      }
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, []);

  const verifyOtp = useCallback(async (email, code) => {
    try {
      const { data } = await api.post('/auth/verify-otp', { email, code });
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, []);

  const resendOtp = useCallback(async (email) => {
    try {
      await api.post('/auth/resend-otp', { email });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore
    }
    setUser(false);
  }, []);

  const value = {
    user,
    isAuthenticated: user && typeof user === 'object',
    isLoading: user === null,
    loginStep1,
    verifyOtp,
    resendOtp,
    logout,
    refreshMe,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
